
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { TechnologistFormOrder } from '@/interfaces/components/projectNew/TechnologistFormOrder';
import ProductSystem from '@/models/ProductSystem';
import { getSystemGroups, SystemGroup } from '@/helpers/Products/ProductHelper';
import ProductCategory from '@/models/ProductCategory';
import { orderAttachmentTypeOptions } from '@/enums/global/OrderAttachmentType';
import AttachmentAPI from '@/api/AttachmentAPI';
// @ts-ignore
import downloadFile from 'downloadjs';

@Component({
    name: 'TechnologistOrderFormOrders',
    components: {},
})
export default class TechnologistOrderFormOrders extends Vue {
    @Prop({ required: true }) private orders!: TechnologistFormOrder[];
    @Prop({ required: true }) private systems!: ProductSystem[];

    private async beforeMount() {
        if (getSystemGroups().length === 0) {
            try {
                await ProductCategory.getAll();
            } catch (e) {
                console.error('Could not load product categories for the system list', e);
            }
        }
        this.selectRequestedSystems();
    }

    @Watch('orders')
    private onOrdersChange() {
        this.selectRequestedSystems();
    }

    private normalizeSystemName(name: string | null | undefined): string {
        return (name ?? '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase();
    }

    private selectRequestedSystems() {
        const options = this.systemGroups.map((group) => group.items).flat();

        (this.orders ?? []).forEach((order) => {
            if (order.system || !order.requestedGroup) {
                return;
            }
            const needle = this.normalizeSystemName(order.requestedGroup);
            const match = options.find((option) => this.normalizeSystemName(option.name) === needle);
            if (match) {
                order.system = match.name;
            }
        });
    }

    private isRequestedSystemSelected(order: TechnologistFormOrder): boolean {
        return this.normalizeSystemName(order.requestedGroup) === this.normalizeSystemName(order.system);
    }

    private get systemGroups(): SystemGroup[] {
        const groups = getSystemGroups();
        if (groups.length) {
            return groups;
        }
        return [{ category: 'System', items: this.systems.map((system) => ({ id: system.id, name: system.name })) }];
    }

    private unlistedSystems(currentName: string | null): ProductSystem[] {
        if (!currentName) {
            return [];
        }
        const listed = this.systemGroups.some((group) => group.items.some((item) => item.name === currentName));
        return listed ? [] : this.systems.filter((system) => system.name === currentName).slice(0, 1);
    }
    @Prop({ required: true }) private orderErrors!: { [key: string]: { system: boolean; quantity: boolean } };

    private addOrder() {
        this.$emit('addOrder');
    }

    private deleteOrder(index: number) {
        this.$emit('deleteOrder', index);
    }

    private setInitialOrders() {
        this.$emit('setInitialOrders');
    }

    private get attachmentTypeOptions() {
        return orderAttachmentTypeOptions;
    }

    private customRequest({ file, onSuccess }: any) {
        setTimeout(() => {
            onSuccess(file);
        }, 1000);
    }

    private async handlePreviewFile(file: any) {
        if (!file.url) {
            return;
        }
        let blob;
        try {
            blob = await AttachmentAPI.getBlob(file.url);
        } catch (e) {
            return;
        }
        // @ts-ignore
        downloadFile(blob.data, file.name);
    }

    private ensureAttachmentRows(order: any) {
        if (!order.attachmentRows || order.attachmentRows.length === 0) {
            this.$set(order, 'attachmentRows', [{ file: null, attachmentType: null }]);
        }
        return order.attachmentRows;
    }

    private hasAnyAttachmentRow(order: any): boolean {
        return (order.attachmentRows ?? []).some((row: any) => row.file);
    }

    private handleOrderAttachmentRowFileChange(order: any, index: number, { fileList }: any) {
        order.attachmentRows[index].file = fileList.length > 0 ? fileList[fileList.length - 1] : null;
        order.attachmentRows[index].existingAttachmentId = null;
    }

    private addOrderAttachmentRow(order: any) {
        order.attachmentRows.push({ file: null, attachmentType: null });
    }

    private onOrderAttachmentRowTrash(order: any, index: number) {
        const row = order.attachmentRows[index];
        if (index === 0 && row) {
            row.file = null;
            row.attachmentType = null;
            row.existingAttachmentId = null;
            return;
        }
        this.removeOrderAttachmentRow(order, index);
    }

    private removeOrderAttachmentRow(order: any, index: number) {
        order.attachmentRows.splice(index, 1);
        if (order.attachmentRows.length === 0) {
            order.attachmentRows.push({ file: null, attachmentType: null });
        }
    }

    private getColorLabel(order: TechnologistFormOrder): string {
        if (!order.colorData) {
            return '';
        }
        return [order.colorData.colorName, order.colorData.powderName].filter(Boolean).join(', ');
    }

    private openColorPicker(index: number) {
        this.$emit('openColorPicker', { target: index });
    }

    private clearColor(order: TechnologistFormOrder) {
        order.colorData = null;
    }

    private async mounted() {
        this.setInitialOrders();
    }
}
